import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Archive, Plus, Search, Package, Users as UsersIcon, Settings as SettingsIcon,
  Camera, ExternalLink, QrCode, FileUp, Box, ArrowRightLeft, CheckCircle2, Clock,
  AlertCircle,
} from 'lucide-react';

const STATUS_COLOR = {
  Available:   'bg-emerald-100 text-emerald-700',
  'In use':    'bg-blue-100 text-blue-700',
  'In repair': 'bg-amber-100 text-amber-700',
  Lost:        'bg-rose-100 text-rose-700',
  Sold:        'bg-slate-100 text-slate-500',
  Scrapped:    'bg-slate-100 text-slate-500',
  Fixed:       'bg-slate-100 text-slate-700',
};

function fmtINR(n) {
  if (n == null || n === '') return '—';
  try { return `₹ ${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹ ${n}`; }
}

function Initials({ name = '?' }) {
  const i = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold">
      {i}
    </span>
  );
}

function PhotoThumb({ src, size = 36 }) {
  return src ? (
    <img src={src} alt="" className="rounded border border-ink/10 object-cover" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded border border-ink/10 bg-page flex items-center justify-center text-ink-2" style={{ width: size, height: size }}>
      <Camera className="h-4 w-4" />
    </div>
  );
}

// Stat card used for the counts strip
function StatCard({ label, value, tone = 'ink', testid }) {
  const toneCls = {
    ink:      'text-ink',
    green:    'text-emerald-700',
    blue:     'text-blue-700',
    amber:    'text-amber-700',
    orange:   'text-orange-700',
  }[tone] || 'text-ink';
  return (
    <Card className="shine-card p-3 rounded-xl flex flex-col gap-1" data-testid={testid}>
      <div className="text-[11px] text-ink-2 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-semibold ${toneCls}`}>{value}</div>
    </Card>
  );
}

function EmptyState({ onAdd, canAdd }) {
  return (
    <Card className="shine-card p-10 rounded-xl flex flex-col items-center justify-center text-center" data-testid="assets-empty">
      <Box className="h-10 w-10 text-ink-2 mb-3" />
      <div className="text-lg font-semibold text-ink">No assets yet.</div>
      <div className="text-sm text-ink-2 mt-1 max-w-md">
        Add the first asset. Everything above ₹ 10,000 — who has it, where it is, and its history.
      </div>
      <div className="flex items-center gap-2 mt-5">
        {canAdd && (
          <Button onClick={onAdd} data-testid="empty-add-asset">
            <Plus className="h-4 w-4 mr-1" /> Add asset
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---------- All assets (default) ----------
function AllAssetsTab({ rows, cats, godowns, canSeeMoney, canAdd, canImport, onAdd, onRowClick, onPrintA4 }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [status, setStatus] = useState('all');
  const [holderType, setHolderType] = useState('all');
  const [godown, setGodown] = useState('all');
  const [owning, setOwning] = useState('all');
  const [showCost, setShowCost] = useState(canSeeMoney);
  const [selected, setSelected] = useState(new Set());

  const owningEntities = useMemo(() => Array.from(new Set(rows.map((r) => r.owning_entity).filter(Boolean))), [rows]);
  const filtered = useMemo(() => rows.filter((r) => {
    if (cat !== 'all' && r.category_id !== cat) return false;
    if (status !== 'all' && (r.status || '') !== status) return false;
    if (holderType !== 'all' && (r.holder_type || '') !== holderType) return false;
    if (godown !== 'all' && r.holder_id !== godown) return false;
    if (owning !== 'all' && (r.owning_entity || '') !== owning) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.name || '').toLowerCase().includes(s)
        || (r.asset_id || '').toLowerCase().includes(s)
        || (r.serial_no || '').toLowerCase().includes(s)
        || (r.holder_name || '').toLowerCase().includes(s);
  }), [rows, q, cat, status, holderType, godown, owning]);

  if (rows.length === 0) {
    return <EmptyState onAdd={onAdd} canAdd={canAdd} />;
  }

  const toggleSel = (aid) => {
    const next = new Set(selected);
    next.has(aid) ? next.delete(aid) : next.add(aid);
    setSelected(next);
  };

  return (
    <Card className="shine-card p-4 rounded-xl" data-testid="assets-all-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 text-ink-2 absolute left-2 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
                 placeholder="Search name, ID or serial" data-testid="assets-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[140px]" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[130px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="Available">Available</SelectItem>
            <SelectItem value="In use">In use</SelectItem>
            <SelectItem value="In repair">In repair</SelectItem>
          </SelectContent>
        </Select>
        <Select value={holderType} onValueChange={setHolderType}>
          <SelectTrigger className="w-[130px]" data-testid="filter-holder"><SelectValue placeholder="Holder" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any holder</SelectItem>
            <SelectItem value="godown">Godown</SelectItem>
            <SelectItem value="user">Person</SelectItem>
          </SelectContent>
        </Select>
        <Select value={godown} onValueChange={setGodown}>
          <SelectTrigger className="w-[150px]" data-testid="filter-godown"><SelectValue placeholder="Godown" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any godown</SelectItem>
            {godowns.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {owningEntities.length > 0 && (
          <Select value={owning} onValueChange={setOwning}>
            <SelectTrigger className="w-[160px]" data-testid="filter-company"><SelectValue placeholder="Company" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any company</SelectItem>
              {owningEntities.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {canSeeMoney && (
          <label className="flex items-center gap-2 text-xs cursor-pointer ml-auto select-none">
            <Switch checked={showCost} onCheckedChange={setShowCost} data-testid="toggle-show-cost" /> Show cost
          </label>
        )}
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-2 mb-3 p-2 rounded-lg bg-blue-50 border border-blue-200">
          <div className="text-xs text-blue-900">{selected.size} selected for print</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
            <Button size="sm" onClick={() => onPrintA4(Array.from(selected))} data-testid="print-a4-sheet">
              Print {selected.size} on an A4 sheet
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="assets-table">
          <thead>
            <tr className="text-ink-2 border-b border-ink/10">
              <th className="w-6 py-2 pr-2">
                <input type="checkbox" aria-label="Select all"
                       checked={filtered.length > 0 && filtered.every((r) => selected.has(r.asset_id))}
                       onChange={(e) => {
                         if (e.target.checked) setSelected(new Set(filtered.map((r) => r.asset_id)));
                         else setSelected(new Set());
                       }}
                       data-testid="select-all" />
              </th>
              <th className="text-left py-2 pr-2"></th>
              <th className="text-left py-2 pr-2">Asset ID</th>
              <th className="text-left py-2 pr-2">Name</th>
              <th className="text-left py-2 pr-2">Category</th>
              <th className="text-left py-2 pr-2">Holder</th>
              <th className="text-left py-2 pr-2">Status</th>
              <th className="text-left py-2 pr-2">Location</th>
              {showCost && <th className="text-right py-2 pr-2">Cost</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const c = cats.find((x) => x.id === r.category_id);
              return (
                <tr key={r.id} className="border-b border-ink/5 hover:bg-page"
                    data-testid={`asset-row-${r.asset_id}`}>
                  <td className="py-2 pr-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${r.asset_id}`}
                           checked={selected.has(r.asset_id)}
                           onChange={() => toggleSel(r.asset_id)}
                           data-testid={`select-${r.asset_id}`} />
                  </td>
                  <td className="py-2 pr-2 cursor-pointer" onClick={() => onRowClick(r)}><PhotoThumb src={(r.photos && r.photos[0])} /></td>
                  <td className="py-2 pr-2 font-mono text-[11px] cursor-pointer" onClick={() => onRowClick(r)}>{r.asset_id}</td>
                  <td className="py-2 pr-2 text-ink font-medium cursor-pointer" onClick={() => onRowClick(r)}>{r.name}</td>
                  <td className="py-2 pr-2 text-ink-2 cursor-pointer" onClick={() => onRowClick(r)}>{c?.name || '—'}</td>
                  <td className="py-2 pr-2 cursor-pointer" onClick={() => onRowClick(r)}>
                    <span className="inline-flex items-center gap-1.5">
                      <Initials name={r.holder_name || 'G'} />
                      <span className="text-ink-2">{r.holder_name || '—'}</span>
                    </span>
                  </td>
                  <td className="py-2 pr-2 cursor-pointer" onClick={() => onRowClick(r)}>
                    <Badge className={STATUS_COLOR[r.status] || 'bg-slate-100 text-slate-600'}>{r.status || '—'}</Badge>
                  </td>
                  <td className="py-2 pr-2 text-ink-2 cursor-pointer" onClick={() => onRowClick(r)}>{r.holder_location || '—'}</td>
                  {showCost && <td className="py-2 pr-2 text-right font-mono cursor-pointer" onClick={() => onRowClick(r)}>{fmtINR(r.purchase_cost)}</td>}
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center text-ink-2 py-6">No matching assets.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------- My assets ----------
function MyAssetsTab({ rows, incoming, onRowClick, onConfirm, onReject, onReturnClick }) {
  if (!rows.length && !incoming.length) {
    return (
      <Card className="shine-card p-8 rounded-xl text-center" data-testid="my-assets-empty">
        <Package className="h-8 w-8 text-ink-2 mx-auto mb-2" />
        <div className="text-sm text-ink">You don't currently hold any assets.</div>
        <div className="text-xs text-ink-2 mt-1">When someone transfers an asset to you, it will appear here.</div>
      </Card>
    );
  }
  return (
    <div className="space-y-4" data-testid="my-assets-tab">
      {incoming.length > 0 && (
        <Card className="shine-card p-4 rounded-xl bg-orange-50 border border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <ArrowRightLeft className="h-4 w-4 text-orange-700" />
            <div className="text-xs font-semibold uppercase tracking-wide text-orange-900">Incoming transfers · {incoming.length}</div>
          </div>
          <div className="space-y-2">
            {incoming.map((a) => {
              const pt = a.pending_transfer || {};
              return (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg bg-white border border-orange-200"
                     data-testid={`incoming-${a.asset_id}`}>
                  <PhotoThumb src={(a.photos && a.photos[0])} size={40} />
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="text-ink">
                      <span className="font-medium">{pt.requested_by_name || '—'}</span> wants to give you:
                      <span className="text-ink"> {a.name}</span>
                      <span className="text-ink-2 font-mono text-xs"> ({a.asset_id})</span>
                    </div>
                    <div className="text-[11px] text-ink-2 mt-0.5">
                      Reason: {pt.reason} · condition {pt.condition}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => onReject(a)} data-testid={`incoming-reject-${a.asset_id}`}>Reject</Button>
                  <Button size="sm" onClick={() => onConfirm(a)} data-testid={`incoming-confirm-${a.asset_id}`}>Confirm receipt</Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {rows.length > 0 && (
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-sm font-semibold mb-2">I hold <span className="text-ink-2 font-normal">· {rows.length} item{rows.length === 1 ? '' : 's'}</span></div>
          <div className="space-y-2">
            {rows.map((r) => {
              const isOverdue = r.expected_return && r.expected_return < new Date().toISOString().slice(0, 10);
              return (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-ink/10 bg-white hover:bg-page"
                     data-testid={`my-asset-${r.asset_id}`}>
                  <div className="flex-1 min-w-0 flex items-center gap-3 cursor-pointer" onClick={() => onRowClick(r)}>
                    <PhotoThumb src={(r.photos && r.photos[0])} size={44} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{r.name}</div>
                      <div className="text-[11px] text-ink-2 font-mono">
                        {r.asset_id} · {r.condition || 'Good'}
                        {r.expected_return && <span> · back by {r.expected_return}</span>}
                      </div>
                    </div>
                  </div>
                  {isOverdue && <Badge className="bg-rose-100 text-rose-700">Overdue</Badge>}
                  <Badge className={STATUS_COLOR[r.status] || 'bg-slate-100 text-slate-600'}>{r.status}</Badge>
                  <Button variant="outline" size="sm" onClick={() => onReturnClick(r)} data-testid={`my-asset-return-${r.asset_id}`}>Return</Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------- Pending transfers (view_all) ----------
function PendingTransfersTab({ rows, onRowClick, onRemind }) {
  if (!rows.length) {
    return <Card className="shine-card p-8 rounded-xl text-center text-sm text-ink-2" data-testid="pending-empty">No pending transfers.</Card>;
  }
  return (
    <Card className="shine-card p-4 rounded-xl" data-testid="pending-transfers-tab">
      <div className="text-sm font-semibold mb-3">Transfers awaiting receiver confirmation</div>
      <div className="space-y-2">
        {rows.map((r) => {
          const pt = r.pending_transfer || {};
          const stale = (r.age_days || 0) >= 2;
          return (
            <div key={r.id} className={`flex items-center gap-3 p-3 rounded-lg border ${stale ? 'border-orange-200 bg-orange-50' : 'border-ink/10 bg-white'}`}
                 data-testid={`pending-${r.asset_id}`}>
              <PhotoThumb src={(r.photos && r.photos[0])} size={40} />
              <div className="flex-1 min-w-0 text-sm cursor-pointer" onClick={() => onRowClick(r)}>
                <div className="text-ink"><span className="font-medium">{r.name}</span><span className="text-ink-2 font-mono text-xs"> ({r.asset_id})</span></div>
                <div className="text-[11px] text-ink-2 mt-0.5">
                  {pt.requested_by_name || '—'} → {pt.to_user_name} · {pt.reason}
                  <span className={stale ? 'text-orange-700 font-semibold' : ''}> · {r.age_days}d old</span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => onRemind(r)} data-testid={`pending-remind-${r.asset_id}`}>Remind</Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- By person (view_all) ----------
function ByPersonTab({ rows, onPersonClick }) {
  if (!rows.length) return null;
  return (
    <Card className="shine-card p-4 rounded-xl" data-testid="by-person-tab">
      <div className="text-sm font-semibold mb-3">Assets held, by person</div>
      <div className="divide-y divide-ink/5">
        {rows.map((u) => (
          <button key={u.id} className="w-full flex items-center gap-3 py-2 px-1 hover:bg-page text-left"
                  onClick={() => onPersonClick(u)}
                  data-testid={`by-person-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-ink font-medium">{u.name}{u.is_head && <Badge className="ml-2 bg-blue-100 text-blue-700 text-[10px]">Head</Badge>}</div>
              <div className="text-[11px] text-ink-2">{u.role}{u.team && ` · ${u.team}`}</div>
            </div>
            <Badge className={u.count ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}>{u.count}</Badge>
          </button>
        ))}
      </div>
    </Card>
  );
}

// ---------- Godown stock ----------
function GodownStockTab({ stock, onRowClick }) {
  if (!stock.length) return null;
  return (
    <div className="space-y-3" data-testid="godown-stock-tab">
      {stock.map((g) => (
        <Card key={g.godown.id} className="shine-card p-4 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-ink">{g.godown.name}</div>
              <div className="text-[11px] text-ink-2">{g.godown.location || ''}</div>
            </div>
            <Badge className="bg-blue-100 text-blue-700">{g.total} item{g.total === 1 ? '' : 's'}</Badge>
          </div>
          <div className="space-y-2">
            {g.groups.map((grp) => (
              <details key={grp.category.id} className="rounded-lg border border-ink/10 bg-white">
                <summary className="flex items-center justify-between px-3 py-2 cursor-pointer text-xs">
                  <span className="text-ink font-medium">{grp.category.name}</span>
                  <span className="text-ink-2">{grp.count}</span>
                </summary>
                <div className="divide-y divide-ink/5">
                  {grp.items.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-page cursor-pointer"
                         onClick={() => onRowClick(r)}>
                      <PhotoThumb src={(r.photos && r.photos[0])} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink truncate">{r.name}</div>
                        <div className="text-[11px] text-ink-2 font-mono">{r.asset_id}</div>
                      </div>
                      <Badge className={STATUS_COLOR[r.manual_status || 'Available'] || 'bg-emerald-100 text-emerald-700'}>Available</Badge>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------- Setup (Stage C: full editing) ----------
function SetupTab({ settings, godowns, cats, onSaved, canManage, users }) {
  const [baseUrl, setBaseUrl] = useState(settings?.public_base_url || '');
  const [owning, setOwning]   = useState((settings?.owning_entities || []).join(', '));
  const [saving, setSaving]   = useState(false);
  const [gList, setGList] = useState(godowns);
  const [cList, setCList] = useState(cats);
  const [newG, setNewG] = useState({ name: '', location: '', keeper_user_ids: [], is_default: false, is_repair: false });
  const [newC, setNewC] = useState({ name: '', code: '' });

  useEffect(() => {
    setBaseUrl(settings?.public_base_url || '');
    setOwning((settings?.owning_entities || []).join(', '));
  }, [settings]);
  useEffect(() => { setGList(godowns); }, [godowns]);
  useEffect(() => { setCList(cats); }, [cats]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/assets/settings', {
        public_base_url: baseUrl.trim(),
        owning_entities: owning.split(',').map((s) => s.trim()).filter(Boolean),
      });
      toast.success('Saved');
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addGodown = async () => {
    try {
      const { data } = await api.post('/assets/godowns', newG);
      setGList([...gList, data]);
      setNewG({ name: '', location: '', keeper_user_ids: [], is_default: false, is_repair: false });
      toast.success('Godown added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const patchGodown = async (g, patch) => {
    try {
      const { data } = await api.patch(`/assets/godowns/${g.id}`, patch);
      setGList(gList.map((x) => (x.id === g.id ? data : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const addCategory = async () => {
    try {
      const { data } = await api.post('/assets/categories', newC);
      setCList([...cList, data]);
      setNewC({ name: '', code: '' });
      toast.success('Category added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const patchCategory = async (c, patch) => {
    try {
      const { data } = await api.patch(`/assets/categories/${c.id}`, patch);
      setCList(cList.map((x) => (x.id === c.id ? data : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const warn = !baseUrl.trim();

  return (
    <div className="space-y-4" data-testid="setup-tab">
      <Card className="shine-card p-5 rounded-xl">
        <div className="text-sm font-semibold mb-1">Public base URL</div>
        <p className="text-[12px] text-ink-2 mb-3">
          Every QR label encodes <code className="bg-page px-1 rounded">{baseUrl || '(not set)'}/a/&lt;asset-id&gt;</code>.
          Printed stickers can&apos;t be changed later — confirm this value before you print real labels.
        </p>
        <div className="space-y-3 max-w-xl">
          <div>
            <Label className="text-xs">Public base URL</Label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} disabled={!canManage}
                   placeholder="https://crm.shineadagency.com" data-testid="setup-base-url" />
            {warn && (
              <p className="text-[11px] text-rose-700 mt-1">
                Required. Without this, the QR endpoint returns an error instead of a relative path.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Owning companies (comma separated)</Label>
            <Input value={owning} onChange={(e) => setOwning(e.target.value)} disabled={!canManage}
                   placeholder="Shine Ad Agency, Shining DOOH LLP, SHINE ADS N EVENTS"
                   data-testid="setup-owning" />
          </div>
          {canManage && <Button onClick={save} disabled={saving} data-testid="setup-save">{saving ? 'Saving…' : 'Save'}</Button>}
        </div>
      </Card>

      <Card className="shine-card p-5 rounded-xl" data-testid="setup-godowns">
        <div className="text-sm font-semibold mb-3">Godowns</div>
        <div className="space-y-2">
          {gList.map((g) => (
            <GodownRow key={g.id} g={g} users={users} canManage={canManage} onSave={(p) => patchGodown(g, p)} />
          ))}
        </div>
        {canManage && (
          <div className="mt-3 pt-3 border-t border-ink/10">
            <div className="text-xs font-semibold mb-2">Add a godown</div>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-sm">
              <Input placeholder="Name" value={newG.name} onChange={(e) => setNewG({ ...newG, name: e.target.value })} data-testid="new-godown-name" />
              <Input placeholder="Location" value={newG.location} onChange={(e) => setNewG({ ...newG, location: e.target.value })} />
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={newG.is_default} onChange={(e) => setNewG({ ...newG, is_default: e.target.checked })} /> Default</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={newG.is_repair} onChange={(e) => setNewG({ ...newG, is_repair: e.target.checked })} /> Repair</label>
              <Button onClick={addGodown} size="sm" disabled={!newG.name.trim()} data-testid="add-godown">Add</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="shine-card p-5 rounded-xl" data-testid="setup-categories">
        <div className="text-sm font-semibold mb-2">Categories</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          {cList.map((c) => (
            <CategoryRow key={c.id} c={c} canManage={canManage} onSave={(p) => patchCategory(c, p)} />
          ))}
        </div>
        {canManage && (
          <div className="mt-3 pt-3 border-t border-ink/10">
            <div className="text-xs font-semibold mb-2">Add a category</div>
            <div className="flex gap-2 flex-wrap">
              <Input placeholder="Name (e.g. Printer)" value={newC.name} onChange={(e) => setNewC({ ...newC, name: e.target.value })} data-testid="new-cat-name" />
              <Input placeholder="CODE" value={newC.code} onChange={(e) => setNewC({ ...newC, code: e.target.value.toUpperCase() })} className="w-28" data-testid="new-cat-code" />
              <Button size="sm" onClick={addCategory} disabled={!newC.name.trim() || !newC.code.trim()} data-testid="add-cat">Add</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="shine-card p-5 rounded-xl">
        <div className="text-sm font-semibold mb-2">Who can list assets?</div>
        <p className="text-[12px] text-ink-2 mb-3">
          Listers are configured per-user in the <b>Users → Access</b> tab under the <b>Assets</b> group.
          Grant <code className="bg-page px-1 rounded">List / add assets</code> to anyone who should be able to add new assets.
        </p>
        <Button variant="outline" size="sm" asChild data-testid="setup-listers">
          <Link to="/users">Open Users → Access</Link>
        </Button>
      </Card>
    </div>
  );
}

function GodownRow({ g, users, canManage, onSave }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(g.name);
  const [loc, setLoc]   = useState(g.location || '');
  const [keepers, setKeepers] = useState(g.keeper_user_ids || []);
  if (!edit) {
    return (
      <div className="flex items-center gap-2 py-1">
        <span className="text-ink font-medium">{g.name}</span>
        <span className="text-ink-2 text-xs">· {g.location}</span>
        {g.is_default && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Default</Badge>}
        {g.is_repair && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Repair</Badge>}
        <span className="text-[11px] text-ink-2 ml-2">{(g.keeper_user_ids || []).length} keeper{(g.keeper_user_ids || []).length === 1 ? '' : 's'}</span>
        {canManage && <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setEdit(true)}>Edit</Button>}
      </div>
    );
  }
  return (
    <div className="p-3 rounded-lg bg-page border border-ink/10 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
        <Input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Location" />
      </div>
      <div>
        <Label className="text-xs">Keepers</Label>
        <div className="flex flex-wrap gap-1 mt-1">
          {users.map((u) => (
            <button key={u.id} type="button"
                    className={`px-2 py-0.5 text-[11px] rounded-full border ${keepers.includes(u.id) ? 'bg-ink text-white border-ink' : 'border-ink/15 text-ink hover:bg-page'}`}
                    onClick={() => setKeepers(keepers.includes(u.id) ? keepers.filter((x) => x !== u.id) : [...keepers, u.id])}>
              {u.name}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => { onSave({ name, location: loc, keeper_user_ids: keepers }); setEdit(false); }}>Save</Button>
        <Button variant="outline" size="sm" onClick={() => setEdit(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function CategoryRow({ c, canManage, onSave }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(c.name);
  const [code, setCode] = useState(c.code);
  if (!edit) {
    return (
      <div className="px-2 py-1 rounded border border-ink/10 bg-white flex items-center justify-between">
        <span className="text-ink">{c.name}</span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-ink-2 font-mono">{c.code}</span>
          {canManage && <Button variant="ghost" size="sm" onClick={() => setEdit(true)}>Edit</Button>}
        </div>
      </div>
    );
  }
  return (
    <div className="p-2 rounded border border-ink/15 bg-page">
      <div className="flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="w-24 font-mono" />
      </div>
      <div className="text-[10px] text-ink-2 mt-1">Code can only be changed if no assets exist in this category.</div>
      <div className="flex gap-2 mt-2">
        <Button size="sm" onClick={() => { onSave({ name, code }); setEdit(false); }}>Save</Button>
        <Button variant="outline" size="sm" onClick={() => setEdit(false)}>Cancel</Button>
      </div>
    </div>
  );
}

// ---------- Deleted assets (Owner only) ----------
function DeletedAssetsTab({ data, onRestore, onHardDelete }) {
  const soft = data?.soft || [];
  const reserved = data?.reserved || [];
  return (
    <div className="space-y-4" data-testid="deleted-tab">
      <Card className="shine-card p-5 rounded-xl">
        <div className="text-sm font-semibold mb-3">Soft-deleted · {soft.length}</div>
        {soft.length === 0 ? <div className="text-xs text-ink-2">No deleted assets.</div> : (
          <div className="space-y-2">
            {soft.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-ink/10 bg-white" data-testid={`deleted-${a.asset_id}`}>
                <PhotoThumb src={(a.photos && a.photos[0])} />
                <div className="flex-1 min-w-0 text-sm">
                  <div className="text-ink"><span className="font-medium">{a.name}</span> <span className="text-ink-2 font-mono text-xs">({a.asset_id})</span></div>
                  <div className="text-[11px] text-ink-2 mt-0.5">
                    Deleted by {a.deleted_by_name} · {new Date(a.deleted_at).toLocaleString('en-IN')} · Reason: {a.deleted_reason}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => onRestore(a)} data-testid={`restore-${a.asset_id}`}>Restore</Button>
                <Button variant="outline" size="sm" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        onClick={() => onHardDelete(a)} data-testid={`hard-delete-${a.asset_id}`}>Delete permanently</Button>
              </div>
            ))}
          </div>
        )}
      </Card>
      {reserved.length > 0 && (
        <Card className="shine-card p-5 rounded-xl">
          <div className="text-sm font-semibold mb-2">Reserved IDs · {reserved.length}</div>
          <div className="text-[11px] text-ink-2 mb-2">These IDs are permanently reserved and will never be reused.</div>
          <ul className="space-y-1 text-xs text-ink">
            {reserved.map((r) => (
              <li key={r.asset_id} className="font-mono">{r.asset_id} · {r.name_at_deletion} · permanently deleted {new Date(r.hard_deleted_at).toLocaleDateString('en-IN')}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}


// ---------- Main page ----------
export default function AssetsPage() {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [pending, setPending] = useState([]);
  const [byPerson, setByPerson] = useState([]);
  const [stock, setStock] = useState([]);
  const [cats, setCats] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [deleted, setDeleted] = useState({ soft: [], reserved: [] });
  const [heldInactive, setHeldInactive] = useState({ count: 0, items: [], users: [] });
  const [counts, setCounts] = useState({ total: 0, in_godown: 0, in_use: 0, in_repair: 0, pending_transfers: 0 });
  const [loading, setLoading] = useState(true);

  const isOwner = user?.role === 'Owner';
  const canSeeMoney = hasPerm('see_money');
  const canAdd    = hasPerm('assets_list');
  const canManage = hasPerm('assets_manage');
  const canViewAll = hasPerm('assets_view_all') || user?.is_head;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        api.get('/assets'),
        api.get('/assets/me'),
        api.get('/assets/me/incoming'),
        api.get('/assets/godown-stock'),
        api.get('/assets/categories'),
        api.get('/assets/godowns'),
        api.get('/assets/counts'),
        api.get('/assets/settings').catch(() => ({ data: null })),
        api.get('/assets/users').catch(() => ({ data: [] })),
      ];
      if (canViewAll) {
        promises.push(api.get('/assets/pending-transfers').catch(() => ({ data: [] })));
        promises.push(api.get('/assets/by-person').catch(() => ({ data: [] })));
        promises.push(api.get('/assets/held-by-inactive').catch(() => ({ data: { count: 0, items: [], users: [] } })));
      }
      if (isOwner) promises.push(api.get('/assets/deleted').catch(() => ({ data: { soft: [], reserved: [] } })));
      const results = await Promise.all(promises);
      const [a, m, inc, s, c, g, st, se, us, pt, bp, hbi, del] = results;
      setRows(a.data || []);
      setMe(m.data || []);
      setIncoming(inc.data || []);
      setStock(s.data || []);
      setCats(c.data || []);
      setGodowns(g.data || []);
      setUsers(us.data || []);
      setCounts({ ...(st.data || {}), pending_transfers: canViewAll ? ((pt?.data || []).length) : 0 });
      setSettings(se.data);
      if (canViewAll) {
        setPending(pt?.data || []);
        setByPerson(bp?.data || []);
        setHeldInactive(hbi?.data || { count: 0, items: [], users: [] });
      }
      if (isOwner) setDeleted(del?.data || { soft: [], reserved: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load assets');
    } finally { setLoading(false); }
  }, [canViewAll, isOwner]);

  useEffect(() => { load(); }, [load]);

  const onRowClick = (r) => navigate(`/assets/${r.asset_id}`);
  const onAdd = () => navigate('/assets/new');

  const confirmIncoming = async (a) => {
    try { await api.post(`/assets/${a.asset_id}/transfer/confirm`); toast.success('Received'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const rejectIncoming = async (a) => {
    try { await api.post(`/assets/${a.asset_id}/transfer/reject`, { reason: '' }); toast.success('Rejected'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const remindPending = async (r) => {
    try { await api.post(`/assets/${r.asset_id}/transfer/remind`); toast.success('Reminder sent'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const printA4 = async (asset_ids) => {
    try {
      const resp = await api.post('/assets/labels/a4', { asset_ids }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      let msg = 'Failed to generate labels';
      try {
        const t = await e?.response?.data?.text?.();
        if (t) msg = (JSON.parse(t).detail) || msg;
      } catch {}
      toast.error(msg);
    }
  };

  const restoreAsset = async (a) => {
    if (!window.confirm(`Restore ${a.asset_id}?`)) return;
    try { await api.post(`/assets/${a.asset_id}/restore`); toast.success('Restored'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const hardDeleteAsset = async (a) => {
    const confirm1 = window.confirm(`Permanently delete ${a.asset_id}? This cannot be undone.`);
    if (!confirm1) return;
    const confirm2 = window.prompt(`Type the asset ID (${a.asset_id}) to confirm permanent deletion:`);
    if (confirm2 !== a.asset_id) { toast.error('Confirmation did not match'); return; }
    try { await api.post(`/assets/${a.asset_id}/hard-delete`); toast.success('Permanently deleted'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const baseTabs = [
    { key: 'all',    label: 'All assets',   icon: Archive },
    { key: 'mine',   label: 'My assets',    icon: Package },
    { key: 'godown', label: 'Godown stock', icon: Box },
  ];
  const viewAllTabs = canViewAll ? [
    { key: 'pending',   label: 'Pending transfers', icon: ArrowRightLeft, badge: pending.length || undefined },
    { key: 'by-person', label: 'By person',         icon: UsersIcon },
  ] : [];
  const ownerTabs = isOwner ? [
    { key: 'deleted', label: `Deleted${deleted.soft.length ? ` · ${deleted.soft.length}` : ''}`, icon: AlertCircle },
  ] : [];
  const tabs = [...baseTabs, ...viewAllTabs, ...ownerTabs, { key: 'setup', label: 'Setup', icon: SettingsIcon }];

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="assets-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-ink-2" />
            <h1 className="text-xl font-semibold text-ink">Assets</h1>
          </div>
          <p className="text-xs text-ink-2 mt-1 max-w-xl">
            Everything above ₹ 10,000 — who has it, where it is, and its history.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canAdd && (
            <Button onClick={onAdd} data-testid="assets-add" size="sm">
              <Plus className="h-4 w-4 mr-1" /> Add asset
            </Button>
          )}
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <StatCard label="Total assets"      value={counts.total}      testid="stat-total" />
        <StatCard label="In godown"         value={counts.in_godown}  tone="green" testid="stat-godown" />
        <StatCard label="In use"            value={counts.in_use}     tone="blue"  testid="stat-in-use" />
        <StatCard label="In repair"         value={counts.in_repair}  tone="amber" testid="stat-repair" />
        <StatCard label="Pending transfers" value={counts.pending_transfers} tone="orange" testid="stat-pending" />
      </div>

      {/* Tabs row */}
      <div className="flex flex-wrap items-center gap-1 border-b border-ink/10">
        {tabs.map((t) => (
          <button key={t.key}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-t-md -mb-px border-b-2 transition-colors ${
                    tab === t.key ? 'border-ink text-ink font-medium' : 'border-transparent text-ink-2 hover:text-ink'
                  }`}
                  onClick={() => setTab(t.key)}
                  data-testid={`assets-tab-${t.key}`}>
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
            {t.badge > 0 && <Badge className="bg-orange-100 text-orange-700 text-[10px] ml-1">{t.badge}</Badge>}
          </button>
        ))}
      </div>

      {canViewAll && heldInactive.count > 0 && (
        <Card className="shine-card p-3 rounded-xl bg-rose-50 border border-rose-200 flex items-center gap-3" data-testid="held-by-inactive">
          <AlertCircle className="h-4 w-4 text-rose-700" />
          <div className="text-xs text-rose-900 flex-1">
            <b>{heldInactive.count}</b> asset{heldInactive.count === 1 ? '' : 's'} still held by {heldInactive.users.length} inactive user{heldInactive.users.length === 1 ? '' : 's'}:
            {' '}{heldInactive.users.map((u) => u.name).join(', ')}.
            This should normally be zero — ask the user to return them or clear the clearance checklist first.
          </div>
        </Card>
      )}

      {loading ? (
        <Card className="shine-card p-10 rounded-xl text-center text-ink-2">Loading…</Card>
      ) : tab === 'all' ? (
        <AllAssetsTab rows={rows} cats={cats} godowns={godowns}
                      canSeeMoney={canSeeMoney} canAdd={canAdd} canImport={false}
                      onAdd={onAdd} onRowClick={onRowClick} onPrintA4={printA4} />
      ) : tab === 'mine' ? (
        <MyAssetsTab rows={me} incoming={incoming}
                     onRowClick={onRowClick}
                     onConfirm={confirmIncoming}
                     onReject={rejectIncoming}
                     onReturnClick={(r) => navigate(`/assets/${r.asset_id}`)} />
      ) : tab === 'godown' ? (
        <GodownStockTab stock={stock} onRowClick={onRowClick} />
      ) : tab === 'pending' ? (
        <PendingTransfersTab rows={pending} onRowClick={onRowClick} onRemind={remindPending} />
      ) : tab === 'by-person' ? (
        <ByPersonTab rows={byPerson} onPersonClick={(u) => navigate(`/assets/clearance/${u.id}`)} />
      ) : tab === 'deleted' ? (
        <DeletedAssetsTab data={deleted} onRestore={restoreAsset} onHardDelete={hardDeleteAsset} />
      ) : (
        <SetupTab settings={settings} godowns={godowns} cats={cats} users={users}
                  canManage={canManage}
                  onSaved={(s) => setSettings(s)} />
      )}
    </div>
  );
}
